import * as React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Input, Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import { useSelector, useDispatch } from "react-redux";
import { useActiveVats } from "services/vatService";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import SubmitButton from "components/common/SubmitButton";
import { getactiveNominals } from "actions/nominals.action";

const CustomVatCode = ({ value, onChange }) => {
  const { data: activeVats = [] } = useActiveVats();

  const matched = activeVats.find((item) => item.vatCodeId === value);
  const [valueVat, setValueVat] = React.useState(matched || null);

  const handleChange = React.useCallback(
    (e) => {
      setValueVat(e.value);
      onChange?.({ value: e.value });
    },
    [onChange]
  );

  return (
    <ComboBox
      data={activeVats}
      value={valueVat}
      textField="display"
      dataItemKey="vatCodeId"
      onChange={handleChange}
      placeholder="Select VAT Code"
    />
  );
};

const CustomNominalCode = ({ value, onChange }) => {
  const dispatch = useDispatch();
  const allNominals = useSelector(
    (state) => state.nominalList.activenominals?.data ?? []
  );

  const nominallist = React.useMemo(
    () =>
      allNominals.filter((item) => item.subType === "ProfitAndLoss_Expense"),
    [allNominals]
  );

  React.useEffect(() => {
    if (allNominals.length === 0) {
      dispatch(getactiveNominals());
    }
  }, [allNominals.length]);

  const matched = nominallist.find((item) => item.nominalRef === value);
  const [valueNominal, setValueNominal] = React.useState(matched || null);

  const handleChange = React.useCallback(
    (e) => {
      setValueNominal(e.value);
      onChange?.({ value: e.value?.nominalRef });
    },
    [onChange]
  );

  return (
    <ComboBox
      data={nominallist}
      value={valueNominal}
      textField="display"
      dataItemKey="nominalRef"
      onChange={handleChange}
      placeholder="Search Nominal"
    />
  );
};

const EditFeeHeaderItems = ({ cancelEdit, onSubmit, isSubmitting, item }) => {
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const validateForm = (values) => {
    const errors = {};
    if (!values.details || values.details.trim() === "") {
      errors.details = "Details are required";
    }
    return errors;
  };

  return (
    <Dialog
      title={item ? `Edit ${item.details}` : "Add Item"}
      onClose={cancelEdit}
      width={600}
    >
      <Form
        ignoreModified
        onSubmit={onSubmit}
        initialValues={{ active: true, ...item }}
        validator={validateForm}
        render={(formRenderProps) => (
          <>
            <div className="p-3 ps-4">
              <FormElement style={{ maxWidth: 1200 }}>
                <fieldset className="k-form-fieldset">
                  <div className="mb-2">
                    <Label className="label mt-1 mb-2 w-100">Details</Label>
                    <Field name="details" component={Input} />
                    {submitAttempted && formRenderProps.errors?.details && (
                      <div className="fs-sm my-1 text-danger">
                        <i>{formRenderProps.errors.details}</i>
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <Label className="label mt-1 mb-2 w-100">VAT Code</Label>
                    <Field name="vatCode" component={CustomVatCode} />
                  </div>

                  <div className="mb-2">
                    <Label className="label mt-1 mb-2 w-100">
                      Nominal Code
                    </Label>
                    <Field name="nominalRef" component={CustomNominalCode} />
                  </div>

                  <div className="mb-3">
                    <Label className="label mt-1 mb-2 w-100">Net Amount</Label>
                    <Field
                      name="netAmount"
                      component={NumericTextBox}
                      format="n2"
                      spinners={false}
                      style={{ textAlign: "right" }}
                    />
                  </div>

                  <Field name="active" component={Checkbox} label="Active" />
                </fieldset>
              </FormElement>
            </div>

            <div className="dialog-footer">
              <button
                className="common-btn me-2 bgBtnSecondary"
                onClick={cancelEdit}
              >
                Cancel
              </button>
              <SubmitButton
                className="me-3"
                isLoading={isSubmitting}
                disabled={!formRenderProps.allowSubmit}
                onClick={() => {
                  setSubmitAttempted(true);
                  formRenderProps.onSubmit();
                }}
              >
                {item ? "Update" : "Save"}
              </SubmitButton>
            </div>
          </>
        )}
      />
    </Dialog>
  );
};

export default EditFeeHeaderItems;
